// Le bilan annuel, à chaque anniversaire du premier emploi (tous les quarts d'heure réels).
import { PATHS } from '../content/paths';
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { nextRandom } from '../engine/rng';
import type { GameState } from '../engine/state';
import { ageOnDay } from '../engine/time';
import { addJournal, unlockPage } from './actions';
import { levelsSinceRaise, resetRaiseChance } from './career';
import { INFLATION_RATE, monthlySpending, networth, passiveIncomeAverage } from './economy';
import { indexSubscriptions } from './subscriptions';

/**
 * L'augmentation annuelle n'est pas automatique. Sa chance part de 25 %, gagne 5 points par niveau
 * de compétence gagné depuis la dernière augmentation et 5 points par bilan passé sans augmentation ;
 * une augmentation (au bilan ou par une promotion) la ramène à 25 %. Le joueur ne la voit pas.
 * Quand elle arrive, elle vaut entre la moitié et une fois et demie le taux du chemin : en moyenne, ce taux.
 */
export const RAISE_CHANCE = { base: 0.25, perLevel: 0.05, perYear: 0.05 };

/** La chance d'être augmenté au prochain bilan. */
export function raiseChance(state: GameState): number {
  return Math.min(
    1,
    RAISE_CHANCE.base + RAISE_CHANCE.perLevel * levelsSinceRaise(state) + RAISE_CHANCE.perYear * state.yearsWithoutRaise,
  );
}

/** L'augmentation de l'année, arrondie au dixième de pour cent ; 0 s'il n'y en a pas. */
export function drawRaise(state: GameState): number {
  if (nextRandom(state) >= raiseChance(state)) return 0;
  const rate = PATHS[state.path ?? 'work'].raise * (0.5 + nextRandom(state));
  return Math.round(rate * 1000) / 1000;
}

export function annualReview(state: GameState): void {
  state.yearsReviewed += 1;
  const oldSalary = state.salary;
  const levelsGained = levelsSinceRaise(state);
  const raise = drawRaise(state);
  state.salary = round2(state.salary * (1 + raise));
  if (raise > 0) resetRaiseChance(state);
  else state.yearsWithoutRaise += 1;

  // Les prix montent : les dépenses du mois aussi, et les petits plaisirs coûtent plus cher.
  state.priceLevel *= 1 + INFLATION_RATE;
  const spendingBefore = monthlySpending(state);
  for (const expense of Object.values(state.expenses)) {
    if (expense) expense.amount = round2(expense.amount * (1 + INFLATION_RATE));
  }
  indexSubscriptions(state, INFLATION_RATE);
  for (const perk of Object.values(state.perks)) perk.leisure = round2(perk.leisure * (1 + INFLATION_RATE));

  const now = networth(state);
  // Tout ce qui a été mis de côté, rapporté à tout ce qui a été reçu dans l'année.
  const saved = Math.max(0, state.year.saved);
  state.pendingReview = {
    age: ageOnDay(state.birthDate, state.startDate, state.day),
    networth: now,
    networthDelta: now - state.year.networthStart,
    passive: passiveIncomeAverage(state),
    savingsRate: state.year.income > 0 ? saved / state.year.income : 0,
    income: state.year.income,
    saved,
    oldSalary,
    salary: state.salary,
    levelsGained,
    spendingBefore,
    spendingAfter: monthlySpending(state),
  };
  state.year = { startDay: state.day, networthStart: now, income: 0, saved: 0 };
  unlockPage(state, 'inflation');
  addJournal(state, state.salary > oldSalary ? 'journal.raise' : 'journal.noRaise', { salary: fmtEur(state.salary) });
}
